<template>
  <div class="flex flex-column" :class="{ 'lg:mt-40': isBasic }">
    <div id="isSharedJob" class="hidden" :data-shared-job="`${isSharedJob}`" />
    <Notification
      :triggerNotification="$store.getters['applications/showMessageSentNotification']"
      :message="$store.getters['applications/toastNotificationMessage']"
      :customTimeout="5000"
      @toggleVisibility="$store.dispatch('applications/hideMessageSentNotification')"
    />
    <ListSelectionLayout
      backButtonText="All applicants"
      :selectedItemId="selectedApplicationUuid"
      @backButtonClick="navigateToApplication"
    >
      <template #listItems>
        <div class="flex flex-col z-[10] md:bg-white pt-24">
          <h1 class="text-h2 mb-8 md:mb-20 mx-16">Applicants</h1>
          <Tabs
            v-model="tabSelectionIndex"
            :tabs="[
              { title: 'Received', badgeNumber: $store.getters['applications/receivedTotal'] },
              { title: 'Interested', parenthesesNumber: $store.getters['applications/interestedTotal'] },
              { title: 'Not a fit', parenthesesNumber: $store.getters['applications/notAFitTotal'] },
            ]"
            :tabListClasses="['grid-cols-3']"
          />
        </div>
        <div class="mb-[70px] md:mb-0">
          <ApplicationsSkeleton v-if="isLoading" />
          <ApplicantList
            v-else
            :careType="careType"
            :currentTabApplications="applications"
            :navigateToApplication="navigateToApplication"
            :currentTab="getParentInterestFromTabIndex"
            :sortBy="sortBy"
            @sortChanged="handleSortChange"
          />
          <div v-if="hasNextPage" class="p-16">
            <Button value="View More" variant="outlined" class="w-full" :loading="isViewMoreLoading" @click="handleViewMore" />
          </div>
        </div>
      </template>
      <template #selectedListItem>
        <router-view />
      </template>
    </ListSelectionLayout>
  </div>
</template>

<script lang="ts">
import ApplicantList from './applicantsList/ApplicantList.vue';
import ApplicationsSkeleton from './applicantsList/ApplicationsSkeleton.vue';
import Button from '@/components/button/Button.vue';
import ListSelectionLayout from '@/components/list_selection_layout/ListSelectionLayout.vue';
import Notification from '@/components/notification/Notification.vue';
import Tabs from '@/components/tabs/Tabs.vue';
import { FETCH_JOB, FETCH_APPLICATION_DETAILS_STATUS, FETCH_JOB_TOTALS } from '@/graphql/applications/queries';

const defaultFetchCount: number = 10;

type ParentInterestStatus = 'RECEIVED' | 'INTERESTED' | 'NOT_A_FIT';

declare module 'vue/types/vue' {
  interface Vue {
    navigateToNextApplication: () => void;
    goToTabIndex: (tabSelection: ParentInterestStatus) => void;
  }
}

export default {
  name: 'Applications',
  components: {
    ApplicantList,
    ApplicationsSkeleton,
    Button,
    ListSelectionLayout,
    Notification,
    Tabs,
  },
  provide(): any {
    return {
      navigateToNextApplication: this.navigateToNextApplication,
      goToTabIndex: this.goToTabIndex,
    };
  },
  data(): any {
    return {
      isLoading: true,
      isViewMoreLoading: false,
      tabSelectionIndex: 0,
      sortBy: 'RECENT',
      applications: [],
    };
  },
  computed: {
    job() {
      return this.$store.getters['applications/job'];
    },
    isSharedJob(): boolean {
      return this.job.isSharedJob || false;
    },
    isBasic(): boolean {
      return this.$store.getters['account/getAccount']?.isBasic() || false;
    },
    hasNextPage(): boolean {
      if (this.isLoading) return false;
      return this.job.applications?.pageInfo?.hasNextPage || false;
    },
    careType(): String {
      return this.job.careType;
    },
    selectedApplicationUuid(): string {
      return this.$route.params.application_uuid || '';
    },
    getParentInterestFromTabIndex(): ParentInterestStatus {
      switch (this.tabSelectionIndex) {
        case 2:
          return 'NOT_A_FIT';
        case 1:
          return 'INTERESTED';
        default: // 0
          return 'RECEIVED';
      }
    },
  },
  watch: {
    tabSelectionIndex(): void {
      this.isLoading = true;
      this.$store.dispatch('applications/showApplicationLoading', true);
      this.jobQuery(this.getParentInterestFromTabIndex);
    },
  },
  created() {
    if (this.selectedApplicationUuid) {
      // jobs/:job_id/applications/:application_id
      this.jobQueryFromApplicationStatus();
    } else {
      // jobs/:job_id/applications
      this.jobQuery(this.getParentInterestFromTabIndex);
    }
  },
  methods: {
    goToTabIndex(tabSelection: ParentInterestStatus): void {
      switch (tabSelection) {
        case 'NOT_A_FIT':
          this.tabSelectionIndex = 2;
          break;
        case 'INTERESTED':
          this.tabSelectionIndex = 1;
          break;
        default: // 'RECEIVED'
          this.tabSelectionIndex = 0;
          break;
      }
    },
    navigateToApplication(applicationId: string = ''): void {
      // prevent redundant navigation for clicking the selected applicant again
      if (this.selectedApplicationUuid === applicationId) {
        this.$store.dispatch('applications/showApplicationLoading', false);
        return;
      }
      // prevent history on desktop navigation, on mobile create history.
      const navigationType = this.$store.getters['applications/isMobile']() ? 'push' : 'replace';
      this.$router[navigationType]({
        path: `/jobs/${this.$route.params.job_uuid}/applications/${applicationId}`,
      });
    },
    navigateToNextApplication(): void {
      // Wait for the next tick to ensure store is updated
      this.$nextTick(() => {
        const currentIndex = this.applications.findIndex((app) => app.id === this.selectedApplicationUuid);
        const nextApplication = this.applications[currentIndex + 1] || this.applications[0];
        if (nextApplication) {
          this.navigateToApplication(nextApplication.id);
        } else {
          // If no next application, go back to the list view
          this.$router.replace({
            path: `/jobs/${this.$route.params.job_uuid}/applications`,
          });
        }
      });
    },
    handleViewMore(): void {
      this.isViewMoreLoading = true;
      this.jobQuery(this.getParentInterestFromTabIndex, true);
    },
    jobQueryFromApplicationStatus() {
      // if URL already contains an application UUID, use application interest status to tell the job query which interest status to query.
      this.$apollo
        .query({
          query: FETCH_APPLICATION_DETAILS_STATUS,
          fetchPolicy: 'network-only',
          variables: {
            application_uuid: this.selectedApplicationUuid,
          },
        })
        .then(({ data }) => {
          const { parentInterestStatus } = data.applicationDetails;
          this.goToTabIndex(parentInterestStatus);
          this.jobQuery(parentInterestStatus);
        })
        .catch((error) => {
          // @ts-ignore
          console.error('GraphQL query error:', error);
        });
    },
    jobQuery(parentInterestStatus: ParentInterestStatus | null, fetchMore = false): void {
      // null parentInterestStatus argument ^ will fetch all jobs of any status, this is only used for shared jobs right now.
      this.fetchTabNumbers();
      const afterCursor = fetchMore ? this.job.applications.pageInfo.endCursor : null;
      this.$apollo
        .query({
          query: FETCH_JOB,
          fetchPolicy: 'network-only',
          variables: {
            first: defaultFetchCount,
            job_uuid: this.$route?.params?.job_uuid,
            parentInterestStatus,
            sort: this.sortBy,
            after: afterCursor,
          },
        })
        .then(({ data }) => {
          this.$store.commit('applications/setJob', data.job);
          if (fetchMore) {
            this.applications = [...this.applications, ...data.job.applications.edges.map((edge) => edge.node)];
          } else {
            this.applications = data.job.applications.edges.map((edge) => edge.node);
          }
          this.isLoading = false;

          // passes the previous number of applicants to the next .then() to find the view more applicant
          return document.querySelectorAll('.sitter-list li').length;
        })
        .then((prevApplicantListLength: number) => {
          if (this.$store.getters['applications/isMobile']()) {
            this.isViewMoreLoading = false;
            return;
          }
          // desktop - view more select the first of new applicants
          if (this.isViewMoreLoading) {
            const firstViewMoreApplicantId = this.applications[prevApplicantListLength]?.id;
            document.getElementById(`${firstViewMoreApplicantId}`)?.querySelector('button')?.focus();
            this.isViewMoreLoading = false;
            return;
          }
          // desktop - auto select first applicant /jobs/:job_id/applications/
          const hasSelectedSitter: boolean = document.querySelector('.sitter-list .selected-application') !== null;
          if (!hasSelectedSitter) {
            this.navigateToApplication(this.applications[0]?.id);
          }
        })
        .catch((error) => {
          this.isLoading = false;
          // @ts-ignore
          console.error('GraphQL query error:', error);
        });
    },
    fetchTabNumbers(): void {
      this.$apollo
        .query({
          query: FETCH_JOB_TOTALS,
          fetchPolicy: 'network-only',
          variables: {
            job_uuid: this.$route?.params?.job_uuid,
          },
        })
        .then(({ data }) => {
          this.$store.commit('applications/setAllStatusTotal', data.allStatusTotal);
          this.$store.commit('applications/setReceivedTotal', data.receivedTotal);
          this.$store.commit('applications/setInterestedTotal', data.interestedTotal);
          this.$store.commit('applications/setNotAFitTotal', data.notAFitTotal);
        })
        .catch((error) => {
          // @ts-ignore
          console.error('GraphQL query error:', error);
        });
    },
    handleSortChange(newSortValue: string): void {
      this.sortBy = newSortValue;
      this.isLoading = true;
      this.jobQuery(this.getParentInterestFromTabIndex);
    },
  },
};
</script>

<style>
body {
  /* always show scrollbar to prevent layout shift */
  overflow-y: scroll;
}

@media (max-width: 640px) {
  .applications-page ~ #onetrust-consent-sdk {
    display: none;
  }
}
</style>
